/**
 * Gobodo Role Provider
 * 
 * Implements RoleProvider using Gobodo API for persistence.
 * Part of PE-894: Create roleService.ts with Gobodo CRUD
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig, { getCustomSuffix } from '../../config/gobodoConfig';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import {
  Role,
  RoleProvider,
  RoleFilters,
  RoleHierarchyNode,
  CreateRoleRequest,
  UpdateRoleRequest,
} from './roleProvider';

/**
 * Get the Role object type key using the configured custom suffix
 * e.g., "Role__c" or "Role__d" depending on environment
 */
function getRoleTypeKey(): string {
  return `Role${getCustomSuffix()}`;
}

/**
 * Convert Gobodo Decimal object to number
 * Gobodo returns numeric fields as {chars, string, valueType} objects
 */
function toNumber(value: any): number {
  if (typeof value === 'number') return value;
  if (value && typeof value === 'object' && 'string' in value) {
    return Number(value.string) || 0;
  }
  return Number(value) || 0;
}

/**
 * Convert Gobodo wrapped boolean to JavaScript boolean
 * Handles formats: true/false, 0/1, "true"/"false", {valueType: "TRUE"}/{valueType: "FALSE"}
 * @exported for testing
 */
export function gobodoToBoolean(val: any): boolean {
  if (val === null || val === undefined) return false;
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val !== 0;
  if (typeof val === 'string') return val.toLowerCase() === 'true' || val === '1';
  // Gobodo wraps booleans as {valueType: "TRUE"} or {valueType: "FALSE"}
  if (typeof val === 'object' && 'valueType' in val) {
    const vt = String(val.valueType).toUpperCase();
    return vt === 'TRUE' || vt === '1';
  }
  return Boolean(val);
}

/**
 * Serialize Gobodo data - convert Decimal/wrapped objects to primitives
 * Gobodo wraps values in objects like {string: "value", valueType: "text"}
 * @exported for testing
 */
export function serializeGobodoData(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  
  // Handle Gobodo wrapped values - check for valueType indicator
  if ('valueType' in obj) {
    const vt = obj.valueType?.toLowerCase?.() || '';
    
    // Numeric types - convert to number
    if (vt === 'decimal' || vt === 'integer' || vt === 'number' || vt === 'int') {
      const val = obj.string ?? obj.integral ?? obj.value ?? obj.chars;
      return Number(val) || 0;
    }
    
    // Boolean types
    if (vt === 'boolean' || vt === 'bool') {
      const val = obj.string ?? obj.integral ?? obj.value;
      if (typeof val === 'boolean') return val;
      if (typeof val === 'number') return val !== 0;
      if (typeof val === 'string') return val.toLowerCase() === 'true' || val === '1';
      return Boolean(val);
    }
    
    // Text/string types - return as string
    if (vt === 'text' || vt === 'string' || vt === 'varchar' || vt === 'textarea') {
      return obj.string ?? obj.value ?? '';
    }
    
    // Unknown valueType with small object - try to extract value
    if (Object.keys(obj).length <= 3) {
      // Return string if available, otherwise try numeric
      if (obj.string !== undefined) return obj.string;
      if (obj.value !== undefined) return obj.value;
      const numVal = obj.integral ?? obj.chars;
      if (numVal !== undefined) return Number(numVal) || 0;
    }
  }
  
  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(serializeGobodoData);
  }
  
  // Handle regular objects recursively
  const result: any = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      result[key] = serializeGobodoData(obj[key]);
    }
  }
  return result;
}

/**
 * Convert Gobodo object to Role type
 * @exported for use in gobodoUserProvider
 */
export function convertFromGobodoRole(gobodoRole: any): Role {
  // Serialize the entire Gobodo response to convert wrapped Gobodo values
  const serialized = serializeGobodoData(gobodoRole);
  
  // Gobodo stores custom fields in the 'data' property, not 'properties'
  const props = serialized.data || serialized.properties || serialized;
  
  let permissions = [];
  let searchPermissions = undefined;

  try {
    if (props.Permissions__c) {
      const raw = props.Permissions__c;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      permissions = parsed.map((p: any) => ({
        objectId: p.objectId,
        objectName: p.objectName,
        read: gobodoToBoolean(p.read),
        write: gobodoToBoolean(p.write),
        delete: gobodoToBoolean(p.delete)
      }));
    }
  } catch (e) {
    console.warn('[GobodoRoleProvider] Failed to parse Permissions__c:', e);
  }

  try {
    if (props.SearchPermissions__c) {
      const raw = props.SearchPermissions__c;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      searchPermissions = {
        canSearchObjects: gobodoToBoolean(parsed.canSearchObjects),
        canSearchFields: gobodoToBoolean(parsed.canSearchFields),
        canSearchWorkflows: gobodoToBoolean(parsed.canSearchWorkflows),
        canSearchSystemSettings: gobodoToBoolean(parsed.canSearchSystemSettings),
        canSearchUsers: gobodoToBoolean(parsed.canSearchUsers),
        canSearchRoles: gobodoToBoolean(parsed.canSearchRoles),
        canSearchSecurity: gobodoToBoolean(parsed.canSearchSecurity),
        canSearchReports: gobodoToBoolean(parsed.canSearchReports),
        canSearchDashboards: gobodoToBoolean(parsed.canSearchDashboards),
        canSearchProfileSettings: gobodoToBoolean(parsed.canSearchProfileSettings),
        canSearchClients: gobodoToBoolean(parsed.canSearchClients),
        canSearchContacts: gobodoToBoolean(parsed.canSearchContacts),
        canSearchLeads: gobodoToBoolean(parsed.canSearchLeads),
        canSearchOpportunities: gobodoToBoolean(parsed.canSearchOpportunities),
        canSearchCases: gobodoToBoolean(parsed.canSearchCases),
        canSearchFirms: gobodoToBoolean(parsed.canSearchFirms),
        canSearchCyberSweepClients: gobodoToBoolean(parsed.canSearchCyberSweepClients),
        canSearchCSAudits: gobodoToBoolean(parsed.canSearchCSAudits),
        canSearchPAWRecords: gobodoToBoolean(parsed.canSearchPAWRecords),
        canSearchCreditCases: gobodoToBoolean(parsed.canSearchCreditCases),
        canSearchTradelines: gobodoToBoolean(parsed.canSearchTradelines),
        canSearchCreditBlockAccounts: gobodoToBoolean(parsed.canSearchCreditBlockAccounts),
        visibleCategories: Array.isArray(parsed.visibleCategories) ? parsed.visibleCategories : [],
      };
    }
  } catch (e) {
    console.warn('[GobodoRoleProvider] Failed to parse SearchPermissions__c:', e);
  }

  return {
    id: gobodoRole.id?.toString() || gobodoRole.key || `role_${Date.now()}`,
    name: props.Name__c || gobodoRole.label || '',
    description: props.Description__c || '',
    parentRoleId: props.ParentRoleId__c,
    parentRoleName: props.ParentRoleName__c,
    isSystem: props.IsSystem__c || false,
    permissions: permissions,
    searchPermissions: searchPermissions,
    // Convert userCount to number (Gobodo returns Decimal objects)
    userCount: toNumber(props.UserCount__c),
    createdBy: props.CreatedBy__c || 'System',
    createdDate: props.CreatedDate__c || new Date().toISOString(),
    lastModified: props.LastModified__c || new Date().toISOString(),
  };
}

/**
 * Convert Role to Gobodo properties format
 */
function convertToGobodoProperties(role: Partial<Role>): Record<string, any> {
  const props: Record<string, any> = {};
  
  if (role.name !== undefined) props.Name__c = role.name;
  if (role.description !== undefined) props.Description__c = role.description;
  if (role.parentRoleId !== undefined) props.ParentRoleId__c = role.parentRoleId;
  if (role.parentRoleName !== undefined) props.ParentRoleName__c = role.parentRoleName;
  if (role.isSystem !== undefined) props.IsSystem__c = role.isSystem;
  
  // Ensure boolean values when saving permissions
  if (role.permissions !== undefined) {
    const cleanPerms = role.permissions.map(p => ({
      objectId: p.objectId,
      objectName: p.objectName,
      read: Boolean(p.read),
      write: Boolean(p.write),
      delete: Boolean(p.delete)
    }));
    props.Permissions__c = JSON.stringify(cleanPerms);
  }
  
  // Ensure boolean values when saving search permissions
  if (role.searchPermissions !== undefined) {
    const sp = role.searchPermissions as any;
    const cleanSP: any = {};
    for (const key in sp) {
      if (key === 'visibleCategories') {
        cleanSP[key] = sp[key] || [];
      } else if (key.startsWith('can') || key.startsWith('visible')) {
        cleanSP[key] = Boolean(sp[key]);
      } else {
        cleanSP[key] = sp[key];
      }
    }
    props.SearchPermissions__c = JSON.stringify(cleanSP);
  }
  
  if (role.userCount !== undefined) props.UserCount__c = role.userCount;
  if (role.createdBy !== undefined) props.CreatedBy__c = role.createdBy;
  if (role.createdDate !== undefined) props.CreatedDate__c = role.createdDate;
  if (role.lastModified !== undefined) props.LastModified__c = role.lastModified;
  
  return props;
}

/**
 * Build role hierarchy from flat list
 */
function buildRoleHierarchy(roles: Role[]): RoleHierarchyNode[] {
  const roleMap = new Map<string, RoleHierarchyNode>();
  const rootRoles: RoleHierarchyNode[] = [];

  roles.forEach(role => {
    roleMap.set(role.id, { ...role, children: [] });
  });

  roles.forEach(role => {
    const node = roleMap.get(role.id)!;
    if (role.parentRoleId) {
      const parent = roleMap.get(role.parentRoleId);
      if (parent) {
        parent.children.push(node);
      } else {
        rootRoles.push(node);
      }
    } else {
      rootRoles.push(node);
    }
  });

  return rootRoles;
}

/**
 * Gobodo Role Provider Implementation
 * 
 * Connects to Gobodo API for role CRUD operations.
 */
export class GobodoRoleProvider implements RoleProvider {
  private graphId: number;

  constructor() {
    this.graphId = GobodoConfig.graph();
  }

  async getRoles(filters?: RoleFilters): Promise<Role[]> {
    try {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);

      const response = await retryWithBackoff(
        () =>
          objectsApi.getObjectsInGraphByType(this.graphId, getRoleTypeKey(), {
            page: 0,
            size: 100,
            cachekey: ['gobodo-roles'],
            cacheduration: '60',
          }),
        { maxRetries: 3, initialDelayMs: 1000 }
      );

      if (!response || !Array.isArray(response)) {
        return [];
      }

      let roles = response.map(convertFromGobodoRole);

      if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        roles = roles.filter(r =>
          r.name.toLowerCase().includes(query) ||
          r.description?.toLowerCase().includes(query)
        );
      }

      if (filters?.includeSystem === false) {
        roles = roles.filter(r => !r.isSystem);
      }

      if (filters?.parentRoleId) {
        roles = roles.filter(r => r.parentRoleId === filters.parentRoleId);
      }

      return roles.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('[GobodoRoleProvider] Failed to fetch roles from Gobodo:', error);
      return [];
    }
  }

  async getRoleById(id: string): Promise<Role | null> {
    try {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);

      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        // If ID is not numeric, search by name
        const roles = await this.getRoles();
        return roles.find(r => r.id === id || r.name === id) || null;
      }

      const response = await retryWithBackoff(() => objectsApi.getObjectById(numericId), { maxRetries: 3 });
      
      if (!response) {
        return null;
      }

      return convertFromGobodoRole(response);
    } catch (error: any) {
      console.error('[GobodoRoleProvider] Failed to fetch role from Gobodo:', error);
      return null;
    }
  }

  async createRole(request: CreateRoleRequest): Promise<Role> {
    try {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);

      // Lookup parent role name if parentRoleId provided
      let parentRoleName: string | undefined;
      if (request.parentRoleId) {
        const parent = await this.getRoleById(request.parentRoleId);
        parentRoleName = parent?.name;
      }

      const roleProperties = convertToGobodoProperties({
        name: request.name,
        description: request.description,
        parentRoleId: request.parentRoleId,
        parentRoleName,
        isSystem: false,
        permissions: request.permissions || [],
        searchPermissions: request.searchPermissions,
        userCount: 0,
        createdBy: 'Current User',
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString(),
      });

      const payload = new GobodoApi.GraphObjectPayload();
      payload.typekey = getRoleTypeKey();
      payload.label = request.name;
      payload.graphs = [this.graphId];
      payload.properties = roleProperties;

      const response = await retryWithBackoff(() => objectsApi.createObject(payload), { maxRetries: 3 });
      
      return convertFromGobodoRole(response);
    } catch (error) {
      console.error('[GobodoRoleProvider] Failed to create role in Gobodo:', error);
      throw new Error('Failed to create role in Gobodo');
    }
  }

  async updateRole(id: string, updates: UpdateRoleRequest): Promise<Role> {
    try {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);

      // Get existing role
      const existingRole = await this.getRoleById(id);
      if (!existingRole) {
        throw new Error('Role not found');
      }


      // For system roles, only allow updating permissions and searchPermissions
      let finalUpdates: Partial<Role> = {};
      if (existingRole.isSystem) {
        if (updates.permissions) finalUpdates.permissions = updates.permissions;
        if (updates.searchPermissions) finalUpdates.searchPermissions = updates.searchPermissions;
      } else {
        if (updates.name !== undefined) finalUpdates.name = updates.name;
        if (updates.description !== undefined) finalUpdates.description = updates.description;
        if (updates.parentRoleId !== undefined) {
          finalUpdates.parentRoleId = updates.parentRoleId;
          if (updates.parentRoleId) {
            const parent = await this.getRoleById(updates.parentRoleId);
            finalUpdates.parentRoleName = parent?.name;
          } else {
            finalUpdates.parentRoleName = undefined;
          }
        }
        if (updates.permissions !== undefined) finalUpdates.permissions = updates.permissions;
        if (updates.searchPermissions !== undefined) finalUpdates.searchPermissions = updates.searchPermissions;
      }

      finalUpdates.lastModified = new Date().toISOString();

      const roleProperties = convertToGobodoProperties(finalUpdates);

      const payload = new GobodoApi.GraphObjectPayload();
      payload.typekey = getRoleTypeKey();
      payload.label = updates.name || existingRole.name;
      payload.graphs = [this.graphId];
      payload.properties = roleProperties;

      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        throw new Error('Invalid role ID for update');
      }

      await retryWithBackoff(() => objectsApi.updateObject(numericId, payload), { maxRetries: 3 });

      // Return updated role
      const updated = await this.getRoleById(id);
      if (!updated) {
        throw new Error('Failed to retrieve updated role');
      }
      return updated;
    } catch (error) {
      console.error('[GobodoRoleProvider] Failed to update role in Gobodo:', error);
      throw error;
    }
  }

  async deleteRole(id: string): Promise<void> {
    try {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);

      // Get existing role
      const role = await this.getRoleById(id);
      if (!role) {
        throw new Error('Role not found');
      }

      if (role.isSystem) {
        throw new Error('Cannot delete system roles');
      }

      if (role.userCount > 0) {
        throw new Error(`Cannot delete role with ${role.userCount} assigned user${role.userCount > 1 ? 's' : ''}`);
      }

      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        throw new Error('Invalid role ID for delete');
      }

      await retryWithBackoff(() => objectsApi.deleteObject(numericId), { maxRetries: 3 });
    } catch (error) {
      console.error('[GobodoRoleProvider] Failed to delete role from Gobodo:', error);
      throw error;
    }
  }

  async cloneRole(id: string, newName: string): Promise<Role> {
    try {
      const original = await this.getRoleById(id);
      if (!original) {
        throw new Error('Role not found');
      }

      return this.createRole({
        name: newName || `${original.name} (Copy)`,
        description: original.description,
        parentRoleId: original.parentRoleId,
        permissions: [...original.permissions],
        searchPermissions: original.searchPermissions ? { ...original.searchPermissions } : undefined,
      });
    } catch (error) {
      console.error('[GobodoRoleProvider] Failed to clone role in Gobodo:', error);
      throw error;
    }
  }

  async getRoleHierarchy(): Promise<RoleHierarchyNode[]> {
    const roles = await this.getRoles();
    return buildRoleHierarchy(roles);
  }
}

// Export singleton instance
export const gobodoRoleProvider = new GobodoRoleProvider();

// Export factory for custom configuration
export function createGobodoRoleProvider(): GobodoRoleProvider {
  return new GobodoRoleProvider();
}
